import 'regenerator-runtime/runtime';
import { buffers, channel, Channel, SagaIterator, Task } from 'redux-saga';
import { AnyAction } from 'redux';
import {
  actionChannel,
  all,
  call,
  cancel,
  cancelled,
  delay,
  flush,
  fork,
  put,
  race,
  select,
  take,
  takeLatest,
} from 'redux-saga/effects';
import {
  GlobalAction,
  GlobalActions,
  ModalType,
  StreckenTarifierungRequestEvents,
} from '@ps20-frontend/shop-integration-redux';
import { App, AppSubroute } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { Dialogschritt } from '@ps20/bff-models';
import {
  TkvV2ChildState,
  loadsProductTable,
  tkvV2Actions,
  tkvV2CoverageType,
  tkvV2LoadsTableOfCoverage,
  tkvV2Produkttabelle,
  tkvV2TotalPrice,
} from '../../app/angebot/children/tkv-v2.adapter';
import { IAppState } from '../appState';
import { buildProduktSnapshots } from '../mappers/produktSnapshots';
import { ChildState, hasLoadedProdukt } from '../mappers/childStates';
import { childKeyOf, childOfKey } from '../models/childKeys';
import { hasPricedSelectedLinie, priceOfSelectedLinie } from '../models/childPricing';
import { isOfferPriced, notDeferredChildKeys, selectableChildKeys } from '../models/angebotOffers';
import { TgvProduktSnapshot } from '../models/angebot.model';
import { answeredKeysOf, keepsPricingIssueKey } from '../models/pricingIssue';
import { selectPricingIssue } from '../selector/pricingIssue.selector';
import {
  ChildPricingForwardQueuedPayload,
  PricingIssueAnsweredPayload,
  TgvActions,
  TgvPricingIssueKind,
  TgvPricingStatus,
} from '../tgvActions';
import { PREISE_FAILED_EVENTS, abortGroup, isNavigationToDashboard } from './groupFailureSaga';
import { nextRequestId } from './requestIds';
import { dispatchToStoreOf, findStoreState, sharedDispatcher } from './sharedDispatcher';

export const MAX_PARALLEL_PRICINGS = 5;
export const REQUEST_TIMEOUT_ASSUMPTION_MS = 30000;
export const RETRY_TIMES = 4;
export const RETRY_DELAY_MS = 2000;
export const ERROR_POLICY = 'report';
export const SLOT_MARGIN_MS = 10000;
export const SLOT_SAFETY_MS =
  RETRY_TIMES * REQUEST_TIMEOUT_ASSUMPTION_MS + (RETRY_TIMES - 1) * RETRY_DELAY_MS + SLOT_MARGIN_MS;
export const MAX_PRICING_ATTEMPTS = 2;
export const IDLE_BUDGET_MS = 2 * SLOT_SAFETY_MS + SLOT_MARGIN_MS;
export const RELOAD_BUDGET_MS = 10000 + 20000;
export const SHOP_MODAL_STATUS_TIMEOUT_MS = 2000;
export const STUCK_PRICING_MS = 60000;
export const LOADING_RECHECK_MS = 1000;

export const PREISE_REQUESTED: Record<string, string> = {
  [App.TKV_V2]: 'PREISE_REQUESTED',
  [App.THH_HUND]: 'THH_HUND_PREISE_REQUESTED',
  [App.THH_PFERD]: 'THH_PFERD_PREISE_REQUESTED',
};

const PREISE_SUCCESSFUL_EVENTS = new Set<string>([
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_TKV_V2,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_TKV_V2,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_THH_HUND,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_THH_HUND,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_MIT_INFO_THH_PFERD,
  StreckenTarifierungRequestEvents.PREISE_REQUESTED_SUCCESSFUL_OHNE_INFO_THH_PFERD,
]);

interface Slot {
  deadline: number;
  unknown: boolean;
  requestId?: string;
  reload?: boolean;
}

type Slots = Map<string, Slot>;

type QueuedForward = ChildPricingForwardQueuedPayload;

export const pricingRequestOf = (inner: AnyAction, requestId: string): AnyAction => ({
  ...inner,
  retry: { times: RETRY_TIMES, delay: RETRY_DELAY_MS },
  errorPolicy: ERROR_POLICY,
  requestId,
});

const hasWaitingChange = (context: SchedulerContext, key: string | null): boolean =>
  key !== null && context.forwardQueue.some(entry => entry.key === key);

const isReloadOpen = (pricing: TransientState['pricing'], key: string): boolean =>
  pricing[key]?.reloadOpen === true;

const isSameChange = (waiting: AnyAction, next: AnyAction): boolean =>
  waiting.type === next.type && waiting.payload?.name === next.payload?.name;

const enqueueForward = (queue: QueuedForward[], next: QueuedForward): void => {
  const latest = queue.map(entry => entry.key).lastIndexOf(next.key);
  if (latest >= 0 && isSameChange(queue[latest].inner, next.inner)) {
    queue[latest] = next;
  } else {
    queue.push(next);
  }
};

const logDroppedForwards = (queue: QueuedForward[]): void => {
  if (queue.length > 0) {
    const keys = Array.from(new Set(queue.map(entry => entry.key)));
    PS20Logger.info(
      'TGV_FORWARDED_PRICING_DROPPED',
      `Dropped the waiting changes of ${keys.join(', ')}: the pricing scheduler ended before a slot freed`,
      App.TGV,
    );
  }
};

const requestQuoteDb = (produkte: TgvProduktSnapshot[]): void => {
  const ausloeser = produkte.at(-1);
  sharedDispatcher().dispatch(
    new GlobalActions().quoteDBInit({
      ausloeserAngebotsId: { appId: ausloeser.app, angebotsId: ausloeser.angebotsId },
      dialogschritt: Dialogschritt.SEITE_1_ANGEBOT_BERECHNEN,
      fehlerCodeTaas: '',
    }),
  );
};

const isFailureEvent = (action: AnyAction): boolean =>
  PREISE_FAILED_EVENTS.includes(action.type) ||
  action.type === GlobalActions.STRECKEN_PRICING_FAILED;

const isSilentFailure = (action: AnyAction): boolean =>
  action.type === GlobalActions.STRECKEN_PRICING_FAILED ||
  action.doRequestAction?.payload?.originEvent?.errorPolicy === ERROR_POLICY;

const isPricingEnd = (action: AnyAction): boolean =>
  PREISE_SUCCESSFUL_EVENTS.has(action.type) || isFailureEvent(action);

const isSchedulerEvent = (action: AnyAction): boolean =>
  isPricingEnd(action) || action.type === GlobalActions.PRODUKT_RECEIVED;

const isStop = (action: AnyAction): boolean =>
  action.type === TgvActions.TGV_ANGEBOT_CLOSED ||
  action.type === TgvActions.TGV_ZUM_ANGEBOT_CLICKED ||
  isNavigationToDashboard(action);

const keyOfPricingEnd = (action: AnyAction): string | null => {
  const payload =
    action.type === GlobalActions.STRECKEN_PRICING_FAILED
      ? action.payload
      : action.doRequestAction?.payload;
  return payload ? childKeyOf({ appId: payload.appId, angebotsId: payload.angebotsId }) : null;
};

const requestIdOfPricingEnd = (action: AnyAction): string | undefined =>
  action.type === GlobalActions.STRECKEN_PRICING_FAILED
    ? action.payload?.requestId
    : action.doRequestAction?.payload?.originEvent?.requestId;

const errorCodeOfPricingEnd = (action: AnyAction): string | undefined =>
  action.type === GlobalActions.STRECKEN_PRICING_FAILED
    ? action.payload?.fehlerCode
    : action.payload?.response?.data?.taasStatus?.fehlerCode;

const keyOfProduktReceived = (action: AnyAction): string | null =>
  action.payload
    ? childKeyOf({ appId: action.payload.appId, angebotsId: action.payload.angebotsId })
    : null;

type SchedulerMode = 'legacy' | 'angebot';

type ReportedFailureOrigin = 'pricing' | 'reload';

interface PendingFailure {
  origin: ReportedFailureOrigin;
  errorCode?: string;
}

interface SchedulerContext {
  mode: SchedulerMode;
  quoteDbRequested: boolean;
  announcedTotal: number | null;
  repriceAfterReload: Set<string>;
  seededKeys: Set<string>;
  forwardQueue: QueuedForward[];
  handOvers: Channel<AnyAction> | null;
  announcedKeys: Set<string>;
  failureShownByProduct: boolean;
  reportedFailures: Map<string, ReportedFailureOrigin>;
  stuckKeys: Set<string>;
  pendingFailures: Map<string, PendingFailure>;
  stuckPending: boolean;
  reportSignals: Channel<boolean>;
  reportFlusher?: Task;
  reportingEnded: boolean;
  lastProgress: number;
}

const isLegacyStop = (action: AnyAction): boolean =>
  isStop(action) || action.type === TgvActions.TGV_ANGEBOT_OPENED;

const isAngebotStop = (action: AnyAction): boolean =>
  isStop(action) || action.type === TgvActions.TGV_ANGEBOT_REQUESTED;

const isHandOver = (action: AnyAction): boolean =>
  action.type === TgvActions.TGV_CHILD_PRICING_FORWARD_QUEUED;

const isAngebotSchedulerEvent = (action: AnyAction): boolean =>
  isSchedulerEvent(action) ||
  isHandOver(action) ||
  action.type === TgvActions.TGV_CHILD_PRICING_FORWARDED ||
  action.type === TgvActions.TGV_CHILD_LINKS_HEALED ||
  action.type === TgvActions.TGV_PRICING_ISSUE_ANSWERED;

function* absorbHandOvers(
  handOvers: Channel<AnyAction> | null,
  queue: QueuedForward[],
): SagaIterator {
  if (handOvers === null) {
    return;
  }
  const buffered: AnyAction[] = yield flush(handOvers);
  buffered.forEach(action => enqueueForward(queue, action.payload));
}

export function* watchPricingScheduler(): SagaIterator {
  yield takeLatest(TgvActions.TGV_ANGEBOT_REQUESTED, safeSaga(pricingSchedulerSaga, App.TGV));
  yield takeLatest(TgvActions.TGV_ANGEBOT_OPENED, safeSaga(angebotPricingSchedulerSaga, App.TGV));
}

export function* pricingSchedulerSaga(): SagaIterator {
  yield race({ scheduling: call(schedule, 'legacy'), stopped: take(isLegacyStop) });
}

export function* angebotPricingSchedulerSaga(): SagaIterator {
  yield race({ scheduling: call(schedule, 'angebot'), stopped: take(isAngebotStop) });
}

const isCoverageTableLoading = (key: string): boolean => {
  const { appId, angebotsId } = childOfKey(key);
  return tkvV2LoadsTableOfCoverage(findStoreState(appId, angebotsId) as TkvV2ChildState);
};

function* resumeOpenReload(
  key: string,
  slots: Slots,
  context: SchedulerContext,
  selectable: string[],
  pricing: TransientState['pricing'],
): SagaIterator<boolean> {
  if (context.mode !== 'angebot' || !selectable.includes(key) || !isReloadOpen(pricing, key)) {
    return false;
  }
  if (isCoverageTableLoading(key)) {
    yield* registerReload(key, slots, pricing);
    PS20Logger.info(
      'TGV_PRODUCT_RELOAD_AWAITED',
      `The coverage reload of ${key} is still loading its product table; it is not sent again`,
      App.TGV,
    );
    return true;
  }
  if (yield* reloadAgain(key, slots, pricing)) {
    PS20Logger.info(
      'TGV_PRODUCT_RELOAD_SENT_AGAIN',
      `The coverage reload of ${key} has not delivered its product table; it is sent again`,
      App.TGV,
    );
    return true;
  }
  yield put(new TgvActions().productReloadEnded(key));
  return false;
}

function* resumePricings(slots: Slots, context: SchedulerContext): SagaIterator {
  const { tiergruppe, transientState }: IAppState = yield select();
  const selectable = selectableChildKeys(tiergruppe, transientState);
  for (const key of notDeferredChildKeys(tiergruppe)) {
    const status = transientState.pricing[key]?.status;
    if (context.mode === 'angebot' && (status === 'failed' || status === 'queued')) {
      context.repriceAfterReload.add(key);
      context.seededKeys.add(key);
    }
    if (yield* resumeOpenReload(key, slots, context, selectable, transientState.pricing)) {
      continue;
    }
    if (status === 'failed') {
      yield put(new TgvActions().pricingStatusChanged(key, 'queued'));
    } else if (status === 'running') {
      const { appId, angebotsId } = childOfKey(key);
      if (!hasPricedSelectedLinie(findStoreState(appId, angebotsId))) {
        slots.set(key, {
          deadline: Date.now() + SLOT_SAFETY_MS,
          unknown: false,
          requestId: transientState.pricing[key].requestId,
        });
      }
    }
  }
}

const delayUntil = (deadline: number) => delay(Math.max(0, deadline - Date.now()));

const waitEffects = (
  events: Parameters<typeof take>[0],
  deadline?: number,
  idleDeadline?: number,
) => ({
  event: take(events),
  ...(deadline === undefined ? {} : { expired: delayUntil(deadline) }),
  ...(idleDeadline === undefined ? {} : { idle: delayUntil(idleDeadline) }),
});

const schedulerEventsOf = (mode: SchedulerMode): typeof isSchedulerEvent =>
  mode === 'angebot' ? isAngebotSchedulerEvent : isSchedulerEvent;

const markProgress = (context: SchedulerContext): void => {
  context.lastProgress = Date.now();
  context.stuckPending = false;
};

const idleDeadlineOf = (
  mode: SchedulerMode,
  unpriced: boolean,
  watchStuck: boolean,
  lastProgress: number,
): number | undefined => {
  if (mode === 'legacy') {
    return unpriced ? lastProgress + IDLE_BUDGET_MS : undefined;
  }
  return watchStuck ? lastProgress + STUCK_PRICING_MS : undefined;
};

function* schedule(mode: SchedulerMode): SagaIterator {
  const handOvers: Channel<AnyAction> | null =
    mode === 'angebot' ? yield actionChannel(isHandOver) : null;
  const events = yield actionChannel(schedulerEventsOf(mode));
  const forwardQueue: QueuedForward[] = [];
  try {
    const slots: Slots = new Map();
    const context: SchedulerContext = {
      mode,
      quoteDbRequested: false,
      announcedTotal: null,
      repriceAfterReload: new Set(),
      seededKeys: new Set(),
      forwardQueue,
      handOvers,
      announcedKeys: new Set(),
      failureShownByProduct: false,
      reportedFailures: new Map(),
      stuckKeys: new Set(),
      pendingFailures: new Map(),
      stuckPending: false,
      reportSignals: channel<boolean>(buffers.sliding(1)),
      reportingEnded: false,
      lastProgress: 0,
    };
    if (mode === 'angebot') {
      context.reportFlusher = yield fork(flushPendingReports, context);
    }
    yield call(resumePricings, slots, context);
    context.lastProgress = Date.now();
    while (true) {
      const { finished, unpriced, started, watchStuck, recheck }: Evaluation = yield call(
        evaluate,
        slots,
        context,
      );
      if (finished) {
        return;
      }
      if (started) {
        markProgress(context);
      }
      const deadline = nextDeadline(slots, recheck);
      const idleDeadline = idleDeadlineOf(mode, unpriced, watchStuck, context.lastProgress);
      const { event, idle } = yield race(waitEffects(events, deadline, idleDeadline));
      if (event) {
        if (yield* handleSchedulerEvent(slots, event, context)) {
          return;
        }
      } else if (idle) {
        if (yield* handleIdleBudget(context)) {
          return;
        }
      } else {
        yield call(expireSlots, slots, context);
      }
    }
  } finally {
    events.close();
    yield* absorbHandOvers(handOvers, forwardQueue);
    handOvers?.close();
    logDroppedForwards(forwardQueue);
  }
}

function* handleSchedulerEvent(
  slots: Slots,
  event: AnyAction,
  context: SchedulerContext,
): SagaIterator<boolean> {
  if (event.type !== TgvActions.TGV_PRICING_ISSUE_ANSWERED && !isHandOver(event)) {
    markProgress(context);
  }
  const mustGiveUp: boolean = yield call(handleEvent, slots, event, context);
  if (mustGiveUp) {
    yield call(giveUp);
    return true;
  }
  yield delay(0);
  return false;
}

function* handleIdleBudget(context: SchedulerContext): SagaIterator<boolean> {
  if (context.mode === 'legacy') {
    yield call(giveUpUnlessShownByProduct, context);
    return true;
  }
  yield call(requestStuckReport, context);
  context.lastProgress = Date.now();
  return false;
}

const requestShopModalStatus = (): void => {
  sharedDispatcher().dispatch(new GlobalActions().globalModalStatusRequested());
};

function* isShopModalOpen(): SagaIterator<boolean> {
  const [{ status }]: [{ status?: GlobalAction<boolean> }] = yield all([
    race({
      status: take(GlobalActions.SHOP_MODAL_STATUS_RECIEVED),
      timeout: delay(SHOP_MODAL_STATUS_TIMEOUT_MS),
    }),
    call(requestShopModalStatus),
  ]);
  if (status === undefined) {
    PS20Logger.error(
      App.TGV,
      'TGV_SHOP_MODAL_STATUS_TIMEOUT',
      `No shop modal status arrived within ${SHOP_MODAL_STATUS_TIMEOUT_MS} ms; continuing as if no shop modal were open`,
    );
    return false;
  }
  return status.payload;
}

function* waitForOpenShopModalToClose(): SagaIterator {
  while (yield* isShopModalOpen()) {
    yield take(GlobalActions.CLOSE_MODAL);
  }
}

function* giveUp(): SagaIterator {
  let cancelReported = false;
  try {
    yield call(waitForOpenShopModalToClose);
    sharedDispatcher().dispatch(
      new GlobalActions().requestModal(ModalType.TARIFIERUNG_STRECKE_ERROR),
    );
    sharedDispatcher().dispatch(new GlobalActions().tarifierungHasCancelled());
    cancelReported = true;
    yield take(GlobalActions.CLOSE_MODAL);
    const { tiergruppe }: IAppState = yield select();
    abortGroup(true, tiergruppe);
  } finally {
    if (!cancelReported && (yield cancelled())) {
      sharedDispatcher().dispatch(new GlobalActions().tarifierungHasCancelled());
    }
  }
}

function* giveUpUnlessShownByProduct(context: SchedulerContext): SagaIterator {
  if (!context.failureShownByProduct) {
    yield call(giveUp);
  }
}

const earliestOf = (...deadlines: Array<number | undefined>): number | undefined => {
  const known = deadlines.filter((deadline): deadline is number => deadline !== undefined);
  return known.length === 0 ? undefined : Math.min(...known);
};

const nextDeadline = (slots: Slots, recheck: boolean): number | undefined =>
  earliestOf(
    slots.size === 0 ? undefined : Math.min(...Array.from(slots.values(), slot => slot.deadline)),
    recheck ? Date.now() + LOADING_RECHECK_MS : undefined,
  );

function* registerForwardedPricing(
  slots: Slots,
  action: AnyAction,
  listed: string[],
  pricing: TransientState['pricing'],
): SagaIterator {
  const { key, requestId, reload } = action.payload;
  if (listed.includes(key)) {
    slots.set(key, {
      deadline: Date.now() + (reload ? RELOAD_BUDGET_MS : SLOT_SAFETY_MS),
      unknown: false,
      requestId,
      reload,
    });
    if (pricing[key]?.status !== 'running') {
      const tgvActions = new TgvActions();
      yield put(tgvActions.pricingAttemptsReset(key));
      yield put(tgvActions.pricingStatusChanged(key, 'running', requestId));
    }
  }
}

function* registerReload(
  key: string,
  slots: Slots,
  pricing: TransientState['pricing'],
): SagaIterator {
  yield* registerForwardedPricing(
    slots,
    new TgvActions().childPricingForwarded(key, nextRequestId(key), true),
    [key],
    pricing,
  );
}

function* reloadAgain(
  key: string,
  slots: Slots,
  pricing: TransientState['pricing'],
): SagaIterator<boolean> {
  const { appId, angebotsId } = childOfKey(key);
  const coverageType = tkvV2CoverageType(findStoreState(appId, angebotsId) as TkvV2ChildState);
  if (coverageType === null) {
    return false;
  }
  yield* registerReload(key, slots, pricing);
  dispatchToStoreOf(appId, angebotsId, {
    ...tkvV2Actions.coverageTypeChanged(coverageType),
    tgvManaged: true,
  });
  return true;
}

const deliversTableOfCoverage = (action: AnyAction, key: string): boolean => {
  const produkttabelle = action.payload?.produkttabelle;
  const { appId, angebotsId } = childOfKey(key);
  return (
    !produkttabelle ||
    produkttabelle === tkvV2Produkttabelle(findStoreState(appId, angebotsId) as TkvV2ChildState)
  );
};

function* endReloadOrIgnoreProduct(
  action: AnyAction,
  key: string,
  pricing: TransientState['pricing'],
): SagaIterator<boolean> {
  if (!isReloadOpen(pricing, key)) {
    return true;
  }
  if (!deliversTableOfCoverage(action, key)) {
    PS20Logger.info(
      'TGV_PRODUCT_TABLE_IGNORED',
      `The product table ${action.payload.produkttabelle} does not belong to the coverage of ${key}; its coverage reload stays open`,
      App.TGV,
    );
    return false;
  }
  yield put(new TgvActions().productReloadEnded(key));
  return true;
}

function* handleProduktReceived(
  slots: Slots,
  action: AnyAction,
  listed: string[],
  context: SchedulerContext,
  pricing: TransientState['pricing'],
): SagaIterator {
  const tgvActions = new TgvActions();
  const key = keyOfProduktReceived(action);
  if (key !== null && listed.includes(key)) {
    if (context.mode === 'angebot') {
      if (!(yield* endReloadOrIgnoreProduct(action, key, pricing))) {
        return;
      }
      context.repriceAfterReload.add(key);
      slots.delete(key);
      yield put(tgvActions.pricingStatusChanged(key, 'queued'));
      yield put(tgvActions.pricingAttemptsReset(key));
      return;
    }
    yield put(tgvActions.pricingAttemptsReset(key));
    if (!slots.has(key)) {
      yield put(tgvActions.pricingStatusChanged(key, 'queued'));
    }
  }
}

const isStaleEcho = (echoedRequestId: string | undefined, slot: Slot | undefined): boolean =>
  echoedRequestId !== undefined &&
  slot?.requestId !== undefined &&
  echoedRequestId !== slot.requestId;

type OwnFailure = 'none' | 'shownByProduct' | 'silent';

const kindOfOwnFailure = (action: AnyAction): OwnFailure =>
  isSilentFailure(action) ? 'silent' : 'shownByProduct';

const endStatusOf = (failed: boolean, waiting: boolean): TgvPricingStatus => {
  if (waiting) {
    return 'queued';
  }
  return failed ? 'failed' : 'priced';
};

function* handlePricingEnd(
  slots: Slots,
  action: AnyAction,
  listed: string[],
  context: SchedulerContext,
  waiting: boolean,
  pricing: TransientState['pricing'],
): SagaIterator<OwnFailure> {
  const key = keyOfPricingEnd(action);
  if (key === null || !listed.includes(key)) {
    return 'none';
  }
  if (context.mode === 'angebot' && isReloadOpen(pricing, key)) {
    return 'none';
  }
  const echoedRequestId = requestIdOfPricingEnd(action);
  const slot = slots.get(key);
  if (isStaleEcho(echoedRequestId, slot)) {
    return 'none';
  }
  if (slot !== undefined) {
    context.seededKeys.delete(key);
  }
  const failed = isFailureEvent(action);
  const ownFailure = failed && (slot !== undefined || echoedRequestId === undefined);
  if (slots.delete(key) || !failed) {
    yield put(new TgvActions().pricingStatusChanged(key, endStatusOf(failed, waiting)));
  }
  return ownFailure ? kindOfOwnFailure(action) : 'none';
}

function* handleEvent(
  slots: Slots,
  action: AnyAction,
  context: SchedulerContext,
): SagaIterator<boolean> {
  const { tiergruppe, transientState }: IAppState = yield select();
  const listed = notDeferredChildKeys(tiergruppe);
  if (action.type === TgvActions.TGV_PRICING_ISSUE_ANSWERED) {
    yield* answerPricingIssue(action.payload, slots, context);
    return false;
  }
  if (action.type === TgvActions.TGV_CHILD_PRICING_FORWARDED) {
    yield* registerForwardedPricing(slots, action, listed, transientState.pricing);
    return false;
  }
  if (isHandOver(action)) {
    return false;
  }
  if (action.type === GlobalActions.PRODUKT_RECEIVED) {
    yield* handleProduktReceived(slots, action, listed, context, transientState.pricing);
    return false;
  }
  if (PREISE_SUCCESSFUL_EVENTS.has(action.type)) {
    context.announcedTotal = null;
  }
  const waiting = hasWaitingChange(context, keyOfPricingEnd(action));
  const failure: OwnFailure = yield* handlePricingEnd(
    slots,
    action,
    listed,
    context,
    waiting,
    transientState.pricing,
  );
  context.failureShownByProduct = context.failureShownByProduct || failure === 'shownByProduct';
  if (context.mode === 'angebot' && !waiting) {
    yield* reportOwnFailure(context, failure, action);
  }
  return failure === 'silent' && context.mode === 'legacy';
}

function* reportOwnFailure(
  context: SchedulerContext,
  failure: OwnFailure,
  action: AnyAction,
): SagaIterator {
  const key = keyOfPricingEnd(action);
  if (failure === 'silent') {
    yield* requestFailureReport(context, key, 'pricing', errorCodeOfPricingEnd(action));
  } else if (failure === 'shownByProduct' && key !== null) {
    context.reportedFailures.delete(key);
    context.pendingFailures.delete(key);
    yield put(new TgvActions().pricingIssueKeysWithdrawn([key]));
  }
}

function* isReportable(key: string): SagaIterator<boolean> {
  const { tiergruppe, transientState }: IAppState = yield select();
  return selectableChildKeys(tiergruppe, transientState).includes(key);
}

function* requestFailureReport(
  context: SchedulerContext,
  key: string | null,
  origin: ReportedFailureOrigin,
  errorCode?: string,
): SagaIterator {
  if (key === null) {
    return;
  }
  context.reportedFailures.delete(key);
  context.pendingFailures.delete(key);
  if (yield* isReportable(key)) {
    context.pendingFailures.set(key, { origin, errorCode });
    yield put(context.reportSignals, true);
  }
}

function* requestStuckReport(context: SchedulerContext): SagaIterator {
  const state: IAppState = yield select();
  const { live } = collectLiveChildren(state.tiergruppe, state.transientState);
  if (isStuckWatched(context, state, live)) {
    context.stuckPending = true;
    yield put(context.reportSignals, true);
  }
}

function* flushPendingReports(context: SchedulerContext): SagaIterator {
  while (true) {
    yield take(context.reportSignals);
    yield call(deliverPendingReports, context);
  }
}

function* deliverPendingReports(context: SchedulerContext): SagaIterator {
  if (context.pendingFailures.size === 0 && !context.stuckPending) {
    return;
  }
  yield call(waitForOpenShopModalToClose);
  const { tiergruppe, transientState }: IAppState = yield select();
  const selectable = selectableChildKeys(tiergruppe, transientState);
  const failures = Array.from(context.pendingFailures).filter(
    ([key]) => selectable.includes(key) && transientState.pricing[key]?.status === 'failed',
  );
  const stuckPending = context.stuckPending;
  context.pendingFailures.clear();
  context.stuckPending = false;
  if (failures.length > 0) {
    yield* putFailedReport(context, failures);
  } else if (stuckPending) {
    yield* putStuckReport(context, tiergruppe, transientState);
  }
}

function* putFailedReport(
  context: SchedulerContext,
  failures: Array<[string, PendingFailure]>,
): SagaIterator {
  const keys = failures.map(([key]) => key);
  const errorCodes = Object.fromEntries(
    failures
      .filter(([, { errorCode }]) => errorCode)
      .map(([key, { errorCode }]) => [key, errorCode]),
  );
  failures.forEach(([key, { origin }]) => context.reportedFailures.set(key, origin));
  PS20Logger.info(
    'TGV_PRICING_ISSUE_REPORTED',
    `The pricing of ${keys.join(', ')} failed finally`,
    App.TGV,
  );
  yield put(new TgvActions().pricingIssueReported('failed', keys, errorCodes));
}

function* putStuckReport(
  context: SchedulerContext,
  tiergruppe: IAppState['tiergruppe'],
  transientState: TransientState,
): SagaIterator {
  const { live } = collectLiveChildren(tiergruppe, transientState);
  const keys = waitingKeys(
    selectableChildKeys(tiergruppe, transientState),
    live,
    transientState.pricing,
  );
  if (keys.length > 0) {
    context.stuckKeys = new Set(keys);
    PS20Logger.info(
      'TGV_PRICING_STUCK',
      `No pricing progress for ${keys.join(', ')}; the offers are still waiting`,
      App.TGV,
    );
    yield put(new TgvActions().pricingIssueReported('stuck', keys));
  }
}

const openKeysOf = (
  kind: TgvPricingIssueKind,
  context: SchedulerContext,
  tiergruppe: IAppState['tiergruppe'],
  transientState: TransientState,
): string[] =>
  selectableChildKeys(tiergruppe, transientState).filter(
    key =>
      (kind === 'failed' ? context.reportedFailures.has(key) : context.stuckKeys.has(key)) &&
      keepsPricingIssueKey(kind, transientState.pricing[key]?.status),
  );

function* answerPricingIssue(
  payload: PricingIssueAnsweredPayload,
  slots: Slots,
  context: SchedulerContext,
): SagaIterator {
  if (context.reportingEnded) {
    return;
  }
  const { tiergruppe, transientState }: IAppState = yield select();
  const stored = transientState.pricingIssue;
  if (stored !== null && stored.kind !== payload.kind) {
    return;
  }
  const answered = answeredKeysOf(
    { kind: payload.kind, keys: openKeysOf(payload.kind, context, tiergruppe, transientState) },
    payload,
  );
  if (answered.length === 0) {
    return;
  }
  context.lastProgress = Date.now();
  if (payload.answer === 'leave') {
    yield* leaveAngebotPage(context);
  } else if (payload.answer === 'retry') {
    yield* retryReportedFailures(answered, slots, context, transientState.pricing);
  } else {
    answered.forEach(key => context.stuckKeys.delete(key));
  }
}

function* leaveAngebotPage(context: SchedulerContext): SagaIterator {
  context.reportingEnded = true;
  if (context.reportFlusher) {
    yield cancel(context.reportFlusher);
  }
  sharedDispatcher().dispatch(
    new GlobalActions().navigationRequested({
      appId: App.TGV,
      subroute: AppSubroute.TAA_EINGABE,
    }),
  );
}

function* retryReportedFailures(
  keys: string[],
  slots: Slots,
  context: SchedulerContext,
  pricing: TransientState['pricing'],
): SagaIterator {
  for (const key of keys) {
    const origin = context.reportedFailures.get(key);
    context.reportedFailures.delete(key);
    yield* retryFailedOffer(key, origin, slots, context, pricing);
  }
}

function* retryFailedOffer(
  key: string,
  origin: ReportedFailureOrigin,
  slots: Slots,
  context: SchedulerContext,
  pricing: TransientState['pricing'],
): SagaIterator {
  const tgvActions = new TgvActions();
  if (origin === 'reload') {
    if (yield* reloadAgain(key, slots, pricing)) {
      return;
    }
    yield put(tgvActions.productReloadEnded(key));
  }
  context.repriceAfterReload.add(key);
  yield put(tgvActions.pricingAttemptsReset(key));
  yield put(tgvActions.pricingStatusChanged(key, 'queued'));
}

function* releaseExpiredSlot(
  key: string,
  slot: Slot,
  slots: Slots,
  context: SchedulerContext,
): SagaIterator {
  slots.delete(key);
  context.seededKeys.delete(key);
  if (!slot.reload && hasWaitingChange(context, key)) {
    PS20Logger.info(
      'TGV_PRICING_SLOT_EXPIRED',
      `No pricing result arrived for ${key}; the slot is released for its waiting change`,
      App.TGV,
    );
    yield put(new TgvActions().pricingStatusChanged(key, 'queued'));
    return;
  }
  PS20Logger.info(
    'TGV_PRICING_SLOT_EXPIRED',
    `No pricing result arrived for ${key}; the slot is released and the child marked failed`,
    App.TGV,
  );
  yield put(new TgvActions().pricingStatusChanged(key, 'failed'));
  if (context.mode === 'angebot') {
    yield* requestFailureReport(context, key, slot.reload ? 'reload' : 'pricing');
  }
}

function* expireSlots(slots: Slots, context: SchedulerContext): SagaIterator {
  const now = Date.now();
  for (const [key, slot] of Array.from(slots.entries())) {
    if (slot.deadline > now) {
      continue;
    }
    if (slot.unknown || slot.reload) {
      yield* releaseExpiredSlot(key, slot, slots, context);
    } else {
      slot.unknown = true;
      slot.deadline = now + SLOT_SAFETY_MS;
      PS20Logger.info(
        'TGV_PRICING_SLOT_UNKNOWN',
        `No pricing result arrived for ${key} within the safety timeout; the slot stays occupied`,
        App.TGV,
      );
    }
  }
}

interface Evaluation {
  finished: boolean;
  unpriced: boolean;
  started: boolean;
  watchStuck: boolean;
  recheck: boolean;
}

interface LiveChild {
  key: string;
  appId: App;
  angebotsId?: string;
  state: ChildState;
}

type TransientState = IAppState['transientState'];

const collectLiveChildren = (
  tiergruppe: IAppState['tiergruppe'],
  transientState: TransientState,
): { listedCount: number; live: LiveChild[] } => {
  const listedChildren = selectableChildKeys(tiergruppe, transientState)
    .map(key => ({ key, ...childOfKey(key) }))
    .map(child => ({ ...child, state: findStoreState(child.appId, child.angebotsId) }));
  return {
    listedCount: listedChildren.length,
    live: listedChildren.filter(child => child.state !== undefined),
  };
};

const waitingKeys = (
  keys: string[],
  live: LiveChild[],
  pricing: TransientState['pricing'],
): string[] =>
  keys.filter(key => {
    if (pricing[key]?.status === 'failed') {
      return false;
    }
    const child = live.find(candidate => candidate.key === key);
    return child === undefined || !isOfferPriced(child, pricing);
  });

const isStuckWatched = (context: SchedulerContext, state: IAppState, live: LiveChild[]): boolean =>
  context.mode === 'angebot' &&
  selectPricingIssue(state) === null &&
  waitingKeys(
    selectableChildKeys(state.tiergruppe, state.transientState),
    live,
    state.transientState.pricing,
  ).length > 0;

const shouldSkipReconcile = (
  child: LiveChild,
  status: string | undefined,
  slots: Slots,
  context: SchedulerContext,
): boolean =>
  context.mode === 'angebot' &&
  (slots.has(child.key) ||
    context.repriceAfterReload.has(child.key) ||
    status === 'queued' ||
    status === 'failed');

function* reconcileChild(
  child: LiveChild,
  transientState: TransientState,
  slots: Slots,
  context: SchedulerContext,
): SagaIterator {
  const tgvActions = new TgvActions();
  const status = transientState.pricing[child.key]?.status;
  if (shouldSkipReconcile(child, status, slots, context)) {
    return;
  }
  const priced = hasPricedSelectedLinie(child.state);
  if (priced && status !== 'priced') {
    yield put(tgvActions.pricingStatusChanged(child.key, 'priced'));
  } else if (!priced && status === 'priced') {
    yield* demotePricedWithoutPrice(child, transientState, context);
  }
}

function* demotePricedWithoutPrice(
  child: LiveChild,
  transientState: TransientState,
  context: SchedulerContext,
): SagaIterator {
  const tgvActions = new TgvActions();
  if ((transientState.pricing[child.key]?.attempts ?? 0) >= MAX_PRICING_ATTEMPTS) {
    PS20Logger.info(
      'TGV_PRICING_ATTEMPTS_EXHAUSTED',
      `Pricing of ${child.key} succeeded ${MAX_PRICING_ATTEMPTS} times without a price on the selected Linie; the child is marked failed`,
      App.TGV,
    );
    yield put(tgvActions.pricingStatusChanged(child.key, 'failed'));
    if (context.mode === 'angebot') {
      yield* requestFailureReport(context, child.key, 'pricing');
    }
  } else {
    yield put(tgvActions.pricingStatusChanged(child.key, 'queued'));
  }
}

const priceOfChild = (child: LiveChild): number =>
  child.appId === App.TKV_V2
    ? (tkvV2TotalPrice(child.state as TkvV2ChildState) ?? 0)
    : priceOfSelectedLinie(child.state);

const groupTotalOf = (live: LiveChild[]): number =>
  live.reduce((total, child) => total + priceOfChild(child), 0);

const isGroupSettled = (
  live: LiveChild[],
  slots: Slots,
  pricing: TransientState['pricing'],
): boolean => live.every(child => !slots.has(child.key) && isOfferPriced(child, pricing));

const announceGroupTotal = (total: number): void => {
  sharedDispatcher().dispatch(new GlobalActions().gesamtpreisChanged(total, false, App.TGV));
};

const finishWhenAllPriced = (
  live: LiveChild[],
  hasUnregistered: boolean,
  produkte: TgvProduktSnapshot[],
  context: SchedulerContext,
  settled: boolean,
): boolean => {
  if (
    live.length === 0 ||
    hasUnregistered ||
    !live.every(child => hasPricedSelectedLinie(child.state))
  ) {
    return false;
  }
  const total = groupTotalOf(live);
  if (context.mode !== 'angebot') {
    announceGroupTotal(total);
    requestQuoteDb(produkte);
    return true;
  }
  if (settled && total !== context.announcedTotal) {
    context.announcedTotal = total;
    announceGroupTotal(total);
  }
  if (!context.quoteDbRequested && !live.some(child => context.seededKeys.has(child.key))) {
    context.quoteDbRequested = true;
    requestQuoteDb(produkte);
  }
  return false;
};

const isLoadingProduct = (child: LiveChild): boolean => loadsProductTable(child.appId, child.state);

const hasLoadingChildWithoutSlot = (
  live: LiveChild[],
  slots: Slots,
  context: SchedulerContext,
): boolean =>
  context.mode === 'angebot' &&
  live.some(child => !slots.has(child.key) && isLoadingProduct(child));

const selectEligible = (
  live: LiveChild[],
  slots: Slots,
  context: SchedulerContext,
  current: TransientState,
  announced: string[],
): LiveChild[] =>
  live.filter(
    child =>
      hasLoadedProdukt(child.state) &&
      (!hasPricedSelectedLinie(child.state) || context.repriceAfterReload.has(child.key)) &&
      !slots.has(child.key) &&
      !announced.includes(child.key) &&
      !hasWaitingChange(context, child.key) &&
      !(context.mode === 'angebot' && isLoadingProduct(child)) &&
      current.pricing[child.key]?.status !== 'failed',
  );

function* startPricings(
  eligible: LiveChild[],
  slots: Slots,
  context: SchedulerContext,
  announcedCount: number,
): SagaIterator<boolean> {
  const tgvActions = new TgvActions();
  let started = false;
  for (const child of eligible) {
    if (slots.size + announcedCount >= MAX_PARALLEL_PRICINGS) {
      break;
    }
    const requestId = nextRequestId(child.key);
    slots.set(child.key, { deadline: Date.now() + SLOT_SAFETY_MS, unknown: false, requestId });
    context.repriceAfterReload.delete(child.key);
    yield put(tgvActions.pricingStatusChanged(child.key, 'running', requestId));
    dispatchToStoreOf(
      child.appId,
      child.angebotsId,
      pricingRequestOf({ type: PREISE_REQUESTED[child.appId] }, requestId),
    );
    started = true;
  }
  return started;
}

const refreshAnnouncedKeys = (
  context: SchedulerContext,
  live: LiveChild[],
  slots: Slots,
  pricing: TransientState['pricing'],
): string[] => {
  if (context.mode !== 'angebot') {
    return [];
  }
  const liveKeys = live.map(child => child.key);
  liveKeys
    .filter(key => pricing[key]?.status === 'running')
    .forEach(key => context.announcedKeys.add(key));
  Array.from(context.announcedKeys)
    .filter(key => slots.has(key) || !liveKeys.includes(key))
    .forEach(key => context.announcedKeys.delete(key));
  return Array.from(context.announcedKeys);
};

const waitsForProduct = (
  child: LiveChild | undefined,
  pricing: TransientState['pricing'],
): boolean =>
  child !== undefined && (pricing[child.key]?.status === 'failed' || isLoadingProduct(child));

function* sendForward(
  child: LiveChild,
  inner: AnyAction,
  slots: Slots,
  context: SchedulerContext,
): SagaIterator {
  const tgvActions = new TgvActions();
  const requestId = nextRequestId(child.key);
  slots.set(child.key, { deadline: Date.now() + SLOT_SAFETY_MS, unknown: false, requestId });
  context.repriceAfterReload.delete(child.key);
  yield put(tgvActions.pricingAttemptsReset(child.key));
  yield put(tgvActions.pricingStatusChanged(child.key, 'running', requestId));
  dispatchToStoreOf(child.appId, child.angebotsId, pricingRequestOf(inner, requestId));
}

function* sendWaitingForwards(
  context: SchedulerContext,
  slots: Slots,
  live: LiveChild[],
  announced: string[],
  pricing: TransientState['pricing'],
): SagaIterator<boolean> {
  let sent = false;
  for (const entry of [...context.forwardQueue]) {
    if (slots.size + announced.length >= MAX_PARALLEL_PRICINGS) {
      break;
    }
    const child = live.find(candidate => candidate.key === entry.key);
    if (slots.has(entry.key) || announced.includes(entry.key) || waitsForProduct(child, pricing)) {
      continue;
    }
    context.forwardQueue.splice(context.forwardQueue.indexOf(entry), 1);
    if (child === undefined || !hasLoadedProdukt(child.state)) {
      context.repriceAfterReload.add(entry.key);
      PS20Logger.info(
        'TGV_FORWARDED_PRICING_DROPPED',
        `Dropped the waiting ${entry.inner.type} for ${entry.key}: the offer is gone or has no product`,
        App.TGV,
      );
      continue;
    }
    yield* sendForward(child, entry.inner, slots, context);
    sent = true;
  }
  return sent;
}

function* evaluate(slots: Slots, context: SchedulerContext): SagaIterator {
  yield* absorbHandOvers(context.handOvers, context.forwardQueue);
  const { tiergruppe, transientState }: IAppState = yield select();
  const produkte = buildProduktSnapshots(tiergruppe, findStoreState);
  yield put(new TgvActions().produkteReceived(produkte));
  const { listedCount, live } = collectLiveChildren(tiergruppe, transientState);
  const hasUnregistered = listedCount > live.length;
  const announced = refreshAnnouncedKeys(context, live, slots, transientState.pricing);
  for (const child of live) {
    yield* reconcileChild(child, transientState, slots, context);
  }
  const currentState: IAppState = yield select();
  const current = currentState.transientState;
  const settled = isGroupSettled(live, slots, current.pricing);
  if (finishWhenAllPriced(live, hasUnregistered, produkte, context, settled)) {
    return { finished: true, unpriced: false, started: false, watchStuck: false, recheck: false };
  }
  const unpriced = hasUnregistered || live.some(child => !hasPricedSelectedLinie(child.state));
  const sent: boolean = yield* sendWaitingForwards(
    context,
    slots,
    live,
    announced,
    transientState.pricing,
  );
  const started: boolean = yield* startPricings(
    selectEligible(live, slots, context, current, announced),
    slots,
    context,
    announced.length,
  );
  const watchStuck = isStuckWatched(context, currentState, live);
  const recheck = hasLoadingChildWithoutSlot(live, slots, context);
  return { finished: false, unpriced, started: sent || started, watchStuck, recheck };
}
